import {
  generateTextFromTipTapContent,
  tiptapContentSchema,
} from '../util/tiptap';
import type { Company, CompanyWithRelations } from '../lib';
import { z } from 'zod';
import { optionalEmailSchema, optionalUrlSchema } from '../util/zod';

export const createCompanySchema = z.object({
  name: z.string().trim().min(1, { message: 'Name is required' }),
  description: tiptapContentSchema
    .refine(
      (data) => {
        try {
          const text = generateTextFromTipTapContent(data);
          return text.length > 0;
        } catch {
          return false;
        }
      },
      {
        message: 'Description is required',
        path: [],
      },
    )
    .refine(
      (data) => {
        try {
          const text = generateTextFromTipTapContent(data);
          return text.length <= 5000;
        } catch {
          return false;
        }
      },
      {
        message: 'Description must be less than 5000 characters',
        path: [],
      },
    ),
  country: z.string().min(1, { message: 'Country is required' }),
  imageId: z.number().optional(),
  batch: z.string().min(1, { message: 'Batch is required' }),
  verticals: z.array(z.string()).optional(),
  website: optionalUrlSchema,
  repoLink: optionalUrlSchema,
  pitchLink: optionalUrlSchema,
  telegramLink: optionalUrlSchema,
  discordLink: optionalUrlSchema,
  twitterLink: optionalUrlSchema,
  schedulingLink: z.string().optional().default(''),
  demoDayTelegramLink: optionalUrlSchema.refine(
    (url) => url === undefined || url === null || url.includes('t.me/'),
    {
      message: 'Telegram link should be in the format https://t.me/username',
    },
  ),
  demoDayContactEmail: optionalEmailSchema,
});

export type CreateCompanyBody = z.infer<typeof createCompanySchema>;

export type CreateCompanyResponse = {
  company: Company;
};

export const editCompanySchema = createCompanySchema.extend({
  id: z.number(),
});

export type EditCompanyBody = z.infer<typeof editCompanySchema>;

export type EditCompanyResponse = {
  company: Company;
};

export const listCompaniesQuerySchema = z.object({
  name: z.string().optional(),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
});

export type ListCompaniesQuery = z.infer<typeof listCompaniesQuerySchema>;

export type ListCompaniesResponse = {
  companies: CompanyWithRelations[];
  hasMore: boolean;
  offset: number;
};

export const retrieveCompanyQuerySchema = z
  .object({
    id: z.coerce.number().optional(),
    slug: z.string().optional(),
  })
  .refine(
    (data) => {
      return (
        (data.id != null && data.slug == null) ||
        (data.id == null && data.slug != null)
      );
    },
    {
      message: "Either 'id' or 'slug' must be provided, but not both.",
    },
  );

export type RetrieveCompanyQuery = z.infer<typeof retrieveCompanyQuerySchema>;

export type RetrieveCompanyResponse = {
  company: CompanyWithRelations;
};

export const editCompanyMembersSchema = z.object({
  id: z.number(),
  membersToAdd: z.array(z.number()),
  membersToRemove: z.array(z.number()),
});

export type EditCompanyMembersBody = z.infer<typeof editCompanyMembersSchema>;

export type EditCompanyMembersResponse = {
  success: boolean;
};
